// ─────────────────────────────────────────────────────────────────────────────
// SubmissionStepEditor.tsx — ContentSectionEditPageCareer
// ─────────────────────────────────────────────────────────────────────────────
import React, { useRef } from 'react';
import { SubmissionStepSection, SubmissionHeaderSection, StepItem } from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './components';

const SubmissionStepEditor: React.FC<{
  data:     SubmissionStepSection;
  onChange: (v: SubmissionStepSection) => void;
}> = ({ data, onChange }) => {
  // Frozen on mount — never mutated
  const orig = useRef(data);

  const header     = data.content[0]?.headerSection;
  const origHeader = orig.current.content[0]?.headerSection;

  const setHeader = (p: Partial<SubmissionHeaderSection>) =>
    onChange({
      ...data,
      content: [{
        ...data.content[0],
        headerSection: { ...header, ...p },
      }],
    });

  const setStep = (stepIdx: number, p: Partial<StepItem>) => {
    const updatedItems = header.list.items.map((item, i) =>
      i === stepIdx ? { ...item, ...p } : item
    );
    setHeader({ list: { ...header.list, items: updatedItems } });
  };

  const setStepButton = (stepIdx: number, title: string) => {
    const item = header.list.items[stepIdx];
    setStep(stepIdx, { button: { ...item.button, title } });
  };

  if (!header || !origHeader) return null;

  return (
    <>
      <LabelRow
        label="Header section"
        toggle={{
          checked:  header.visible,
          onChange: v => setHeader({ visible: v }),
        }}
      />

      <LabelRow label="Main content title" required />
      <SplitRow currentText={`[ ${origHeader.label} ]`}>
        <DraftTextField
          placeholder="Main content title"
          onChange={v => setHeader({ label: v })}
        />
      </SplitRow>

      <LabelRow label="Main title" required />
      <SplitRow currentText={origHeader.title}>
        <DraftTextField
          placeholder="Sector title"
          onChange={v => setHeader({ title: v })}
        />
      </SplitRow>

      <LabelRow label="Main description" />
      <SplitRow currentText={origHeader.description} multiline>
        <DraftTextArea
          placeholder="Main description"
          rows={3}
          onChange={v => setHeader({ description: v })}
        />
      </SplitRow>

      <LabelRow
        label="Steps"
        toggle={{
          checked:  header.list.visible,
          onChange: v => setHeader({ list: { ...header.list, visible: v } }),
        }}
      />

      {origHeader.list.items.map((origStep, idx) => (
        <div key={origStep.id} className="scl--cms-content-edit-career-step-block">
          <LabelRow label={`Step ${idx + 1}`} />

          <SplitRow currentText={origStep.title}>
            <DraftTextField
              key={`step-title-${idx}`}
              placeholder="Text tittle"
              onChange={v => setStep(idx, { title: v })}
            />
          </SplitRow>

          <SplitRow currentText={origStep.description} multiline>
            <DraftTextArea
              key={`step-desc-${idx}`}
              placeholder="Tittle description"
              rows={3}
              onChange={v => setStep(idx, { description: v })}
            />
          </SplitRow>

          {origStep.button && (
            <SplitRow currentText={origStep.button.title}>
              <DraftTextField
                key={`step-btn-${idx}`}
                placeholder="Text button"
                onChange={v => setStepButton(idx, v)}
              />
            </SplitRow>
          )}
        </div>
      ))}
    </>
  );
};

export default SubmissionStepEditor;